/* Phase 12A + 12B-1 -- unified floating launcher for Sharda Assistant.
 * Bottom-right sky-blue circle. Two modes:
 *   Ask mode (Help Bot) -- Q&A over the built-in KB. Powered by Claude Haiku.
 *   Do mode (Action Agent) -- runs low-risk tools, proposes medium-risk ones
 *   for user confirmation, blocks destructive ones. Powered by Claude Sonnet.
 *
 * Truth table for which tabs appear:
 *   help=on  / action=off -> only Ask tab
 *   help=off / action=on  -> only Do tab
 *   help=on  / action=on  -> both tabs, Ask default
 *   help=off / action=off -> launcher hidden entirely
 *
 * Hidden on /login, /setup, /invite/accept/*, /tour.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, X, Send, Loader2, MessageSquare, Wand2,
  CheckCircle2, XCircle, AlertTriangle, Mail, Zap,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AiUsageBadge, BudgetExceededModal, useAiUsage } from "@/components/ai/AiUsageBadge";

const LS_KEY_HISTORY = "sharda.ai.ask.history.v1";
const LS_KEY_MODE = "sharda.ai.mode.v1";
const HIDDEN_PREFIXES = ["/login", "/setup", "/invite/accept", "/tour"];

function loadHistory() {
  try {
    const raw = localStorage.getItem(LS_KEY_HISTORY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-20) : [];
  } catch { return []; }
}

function saveHistory(msgs) {
  try { localStorage.setItem(LS_KEY_HISTORY, JSON.stringify(msgs.slice(-20))); } catch { /* noop */ }
}

export default function HelpBotLauncher() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [helpEnabled, setHelpEnabled] = useState(null);      // null = loading
  const [actionEnabled, setActionEnabled] = useState(null);  // null = loading
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(() => localStorage.getItem(LS_KEY_MODE) || "ask");

  // Ask mode state
  const [askBusy, setAskBusy] = useState(false);
  const [askInput, setAskInput] = useState("");
  const [askMessages, setAskMessages] = useState([]);

  // Do mode state
  const [doBusy, setDoBusy] = useState(false);
  const [doInput, setDoInput] = useState("");
  const [doMessages, setDoMessages] = useState([]);

  // Phase 16B -- usage badge + budget-exceeded modal.
  const { usage, refetch: refetchUsage } = useAiUsage();
  const [budgetModal, setBudgetModal] = useState({ open: false, message: "" });

  const scrollRef = useRef(null);

  const hidden = useMemo(() => {
    if (!user?.id) return true;
    return HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));
  }, [user?.id, pathname]);

  /* Phase 17.4 -- Escape key closes the panel. Prevents the panel from
   * getting stuck open and blocking underlying interactions (e.g. the
   * chat send button on narrow viewports). */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const fetchState = () => {
    if (!user?.id) return;
    Promise.all([
      api.get("/ai/help/health").then((r) => !!r.data?.enabled).catch(() => false),
      api.get("/ai/action/health").then((r) => !!r.data?.enabled).catch(() => false),
    ]).then(([hb, aa]) => {
      // D22 -- gate dev diagnostics behind NODE_ENV. This poll fires
      // on focus + every ~5s, so keeping it uncondtional lit up the
      // production console with hundreds of "[Launcher] health" lines
      // per session. `console.log` in prod is a bug, not a feature.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("[Launcher] health", { help: hb, action: aa });
      }
      setHelpEnabled(hb);
      setActionEnabled(aa);
    });
  };

  useEffect(() => {
    fetchState();
    const onFocus = () => fetchState();
    const onConfigChanged = () => fetchState();
    window.addEventListener("focus", onFocus);
    window.addEventListener("ai-help-config-changed", onConfigChanged);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("ai-help-config-changed", onConfigChanged);
    };
  }, [user?.id, pathname]);

  useEffect(() => {
    if (open) setAskMessages(loadHistory());
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [askMessages, doMessages, askBusy, doBusy]);

  // If a mode gets disabled from admin while a tab is active, swap it.
  useEffect(() => {
    if (helpEnabled === false && actionEnabled === true && mode !== "do") setMode("do");
    if (actionEnabled === false && helpEnabled === true && mode !== "ask") setMode("ask");
  }, [helpEnabled, actionEnabled, mode]);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY_MODE, mode); } catch { /* noop */ }
  }, [mode]);

  const anyEnabled = helpEnabled === true || actionEnabled === true;
  if (hidden || !anyEnabled) return null;

  const showAskTab = helpEnabled === true;
  const showDoTab = actionEnabled === true;
  const activeMode = showAskTab && showDoTab
    ? mode
    : (showAskTab ? "ask" : "do");

  // ------------------------------------------------------------
  // Ask handler
  // ------------------------------------------------------------
  const askSend = async () => {
    const text = askInput.trim();
    if (!text || askBusy) return;
    setAskInput("");
    const userMsg = { role: "user", content: text, ts: Date.now() };
    const next = [...askMessages, userMsg];
    setAskMessages(next);
    saveHistory(next);
    setAskBusy(true);
    try {
      const history = next.slice(-10).map((m) => ({ role: m.role, content: m.content }));
      const { data } = await api.post("/ai/help/ask", { message: text, history });
      const asstMsg = {
        role: "assistant", content: data.answer || "(no response)",
        sources: data.sources || [], latency_ms: data.latency_ms, ts: Date.now(),
      };
      const nn = [...next, asstMsg];
      setAskMessages(nn); saveHistory(nn);
    } catch (e) {
      if (e?.response?.status === 429) {
        setBudgetModal({ open: true, message: e?.response?.data?.detail });
      }
      const nn = [...next, {
        role: "assistant",
        content: `**Error:** ${e?.response?.data?.detail || e.message || "Something went wrong"}`,
        error: true, ts: Date.now(),
      }];
      setAskMessages(nn); saveHistory(nn);
    } finally {
      setAskBusy(false);
      refetchUsage();
    }
  };

  // ------------------------------------------------------------
  // Do handler
  // ------------------------------------------------------------
  const doSend = async () => {
    const text = doInput.trim();
    if (!text || doBusy) return;
    setDoInput("");
    const userMsg = { kind: "user", content: text, ts: Date.now() };
    setDoMessages((m) => [...m, userMsg]);
    setDoBusy(true);
    try {
      const { data } = await api.post("/ai/action/execute", { message: text });
      setDoMessages((m) => [...m, { kind: "action", payload: data, ts: Date.now() }]);
    } catch (e) {
      if (e?.response?.status === 429) {
        setBudgetModal({ open: true, message: e?.response?.data?.detail });
      }
      setDoMessages((m) => [...m, {
        kind: "action",
        payload: {
          status: "error",
          message: e?.response?.data?.detail || e.message || "Action failed",
        },
        ts: Date.now(),
      }]);
    } finally {
      setDoBusy(false);
      refetchUsage();
    }
  };

  const doConfirm = async (actionId) => {
    setDoBusy(true);
    try {
      const { data } = await api.post("/ai/action/confirm", { action_id: actionId });
      setDoMessages((m) => m.map((msg) => (
        msg.kind === "action" && msg.payload.action_id === actionId
          ? { ...msg, payload: { ...msg.payload, ...data, status: "executed" } }
          : msg
      )));
    } catch (e) {
      setDoMessages((m) => m.map((msg) => (
        msg.kind === "action" && msg.payload.action_id === actionId
          ? { ...msg, payload: { ...msg.payload, status: "denied", message: e?.response?.data?.detail || e.message } }
          : msg
      )));
    } finally { setDoBusy(false); }
  };

  const doCancel = async (actionId) => {
    try { await api.post("/ai/action/cancel", { action_id: actionId }); } catch { /* silent */ }
    setDoMessages((m) => m.map((msg) => (
      msg.kind === "action" && msg.payload.action_id === actionId
        ? { ...msg, payload: { ...msg.payload, status: "cancelled" } }
        : msg
    )));
  };

  const clearActive = () => {
    if (activeMode === "ask") { setAskMessages([]); saveHistory([]); }
    else { setDoMessages([]); }
  };

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="ai-launcher"
            data-testid="ai-help-launcher"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={() => setOpen(true)}
            /* Dispatch 13 -- CSS handles the mobile lift so the FAB clears
               the 56px + safe-area bottom nav. See theme.css ->
               `.ai-fab-mobile-lift`. Desktop keeps the compact bottom-5. */
            className="ai-fab-mobile-lift fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-sky-400 to-blue-700 shadow-lg shadow-sky-500/40 border border-sky-300/40 flex items-center justify-center text-white hover:scale-105 transition-transform"
            aria-label="Open Sharda Assistant"
          >
            <span className="absolute inset-0 rounded-full bg-sky-400/40 animate-ping opacity-60" />
            <Sparkles className="h-6 w-6 relative z-10" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <>
            {/* Phase 17.4 -- click-outside backdrop. Semi-transparent on mobile
             * so the underlying page stays visually anchored; transparent on
             * desktop where the 440px panel does not cover the workspace.
             * Clicking anywhere on the backdrop closes the panel, restoring
             * access to underlying controls (e.g. chat send button). */}
            <motion.div
              key="ai-panel-backdrop"
              data-testid="ai-help-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[45] bg-black/30 sm:bg-transparent sm:pointer-events-none"
              aria-hidden="true"
            />
            <motion.div
              key="ai-panel"
              data-testid="ai-help-panel"
              initial={{ x: 460, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 460, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              className="fixed inset-y-0 right-0 z-50 w-[92vw] max-w-[440px] sm:w-[440px] bg-[#040D1C]/95 backdrop-blur-2xl border-l border-white/10 shadow-2xl flex flex-col"
            >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-400 to-blue-700 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="font-heading text-sm uppercase tracking-[0.08em]">Sharda Assistant</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="text-[10px] text-foreground/50">
                      {activeMode === "ask" ? "Ask mode - Help Bot" : "Do mode - Action Agent"}
                    </div>
                    <AiUsageBadge usage={usage} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {(activeMode === "ask" ? askMessages : doMessages).length > 0 && (
                  <Button size="sm" variant="ghost" className="text-[10px] h-7"
                          onClick={clearActive} data-testid="ai-help-clear">Clear</Button>
                )}
                <Button size="icon" variant="ghost" className="h-8 w-8"
                        onClick={() => setOpen(false)} data-testid="ai-help-close">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Mode segmented toggle -- only rendered when both are enabled */}
            {showAskTab && showDoTab && (
              <div className="px-4 pt-3 pb-2" data-testid="ai-help-mode-slot">
                <div className="grid grid-cols-2 gap-1 p-1 bg-white/5 border border-white/10 rounded-lg">
                  <button
                    data-testid="ai-mode-ask"
                    onClick={() => setMode("ask")}
                    className={`text-xs py-1.5 rounded-md font-medium transition-all inline-flex items-center justify-center gap-1.5 ${
                      mode === "ask"
                        ? "bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow"
                        : "text-foreground/60 hover:text-foreground/90 hover:bg-white/5"
                    }`}
                  >
                    <MessageSquare className="h-3.5 w-3.5" /> Ask
                  </button>
                  <button
                    data-testid="ai-mode-do"
                    onClick={() => setMode("do")}
                    className={`text-xs py-1.5 rounded-md font-medium transition-all inline-flex items-center justify-center gap-1.5 ${
                      mode === "do"
                        ? "bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white shadow"
                        : "text-foreground/60 hover:text-foreground/90 hover:bg-white/5"
                    }`}
                  >
                    <Wand2 className="h-3.5 w-3.5" /> Do
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4"
                 data-testid={activeMode === "ask" ? "ai-help-messages" : "ai-do-messages"}>
              {activeMode === "ask" && (
                <AskPanel messages={askMessages} busy={askBusy} user={user}
                          onSuggest={(t) => setAskInput(t)} />
              )}
              {activeMode === "do" && (
                <DoPanel messages={doMessages} busy={doBusy} user={user}
                         onSuggest={(t) => setDoInput(t)}
                         onConfirm={doConfirm} onCancel={doCancel} />
              )}
            </div>

            {/* Input row */}
            <div className="p-3 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  data-testid={activeMode === "ask" ? "ai-help-input" : "ai-do-input"}
                  value={activeMode === "ask" ? askInput : doInput}
                  onChange={(e) => activeMode === "ask" ? setAskInput(e.target.value) : setDoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      activeMode === "ask" ? askSend() : doSend();
                    }
                  }}
                  placeholder={activeMode === "ask"
                    ? "Ask anything about the app..."
                    : "Try: 'punch me in', 'show my stats', 'email ceo@shardapower.com about X'"}
                  className="bg-white/5"
                  disabled={activeMode === "ask" ? askBusy : doBusy}
                />
                <Button
                  data-testid={activeMode === "ask" ? "ai-help-send" : "ai-do-send"}
                  onClick={() => activeMode === "ask" ? askSend() : doSend()}
                  disabled={activeMode === "ask" ? (askBusy || !askInput.trim()) : (doBusy || !doInput.trim())}
                  className={`${
                    activeMode === "do"
                      ? "bg-gradient-to-br from-fuchsia-500 to-purple-700"
                      : "bg-gradient-primary"
                  } text-white shrink-0`}
                >
                  {(activeMode === "ask" ? askBusy : doBusy)
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Send className="h-4 w-4" />}
                </Button>
              </div>
              <div className="text-[9px] uppercase tracking-widest text-foreground/40 mt-1.5 text-center">
                {activeMode === "ask"
                  ? "Powered by Claude Haiku via Emergent"
                  : "Powered by Claude Sonnet 4.5 via Emergent"}
              </div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
      <BudgetExceededModal
        open={budgetModal.open}
        message={budgetModal.message}
        usage={usage}
        onClose={() => setBudgetModal({ open: false, message: "" })}
      />
    </>
  );
}

/* --------------------------------------------------------------- */
/* Ask panel body                                                  */
/* --------------------------------------------------------------- */
function AskPanel({ messages, busy, user, onSuggest }) {
  return (
    <>
      {messages.length === 0 && !busy && (
        <div className="text-center text-foreground/55 text-sm py-8">
          <Sparkles className="h-8 w-8 text-sky-400/60 mx-auto mb-3" />
          <p>Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</p>
          <p className="text-xs mt-1 text-foreground/40">Ask me how any Sharda Energy OS feature works.</p>
          <div className="mt-4 space-y-1.5 text-[11px]">
            {["How do I add a new leave type?",
              "Where do I configure SMTP?",
              "How do invite emails work?"].map((t) => (
              <button key={t} type="button" onClick={() => onSuggest(t)}
                data-testid="ai-help-suggestion"
                className="block mx-auto px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-foreground/75">
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
      {messages.map((m, i) => (
        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
          <div
            className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
              m.role === "user"
                ? "bg-gradient-to-br from-sky-500 to-blue-700 text-white"
                : m.error
                ? "bg-rose-500/15 border border-rose-500/30 text-rose-100"
                : "bg-white/5 border border-white/10 text-foreground/90"
            }`}
            data-testid={m.role === "user" ? "ai-help-msg-user" : "ai-help-msg-assistant"}
          >
            <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
            {m.sources?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap gap-1.5">
                {m.sources.map((s, si) => (
                  s.path ? (
                    <a key={si} href={s.path} target="_blank" rel="noreferrer"
                      className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-500/40 text-sky-200 hover:bg-sky-500/30"
                      data-testid={`ai-help-source-${si}`} title={s.snippet}>
                      {s.title || s.source}
                    </a>
                  ) : (
                    <span key={si}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-foreground/60"
                      data-testid={`ai-help-source-${si}`} title={s.snippet}>
                      {s.title || s.source}
                    </span>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      {busy && (
        <div className="flex items-center gap-2 text-xs text-foreground/50" data-testid="ai-help-typing">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>AI is thinking...</span>
        </div>
      )}
    </>
  );
}

/* --------------------------------------------------------------- */
/* Do panel body                                                   */
/* --------------------------------------------------------------- */
function DoPanel({ messages, busy, user, onSuggest, onConfirm, onCancel }) {
  return (
    <>
      {messages.length === 0 && !busy && (
        <div className="text-center text-foreground/55 text-sm py-8" data-testid="ai-do-empty">
          <Wand2 className="h-8 w-8 text-fuchsia-400/60 mx-auto mb-3" />
          <p>Do mode{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</p>
          <p className="text-xs mt-1 text-foreground/40">Say what you want done, in your own words.</p>
          <div className="mt-4 space-y-1.5 text-[11px]">
            {["punch me in",
              "show my stats",
              "email ceo@shardapower.com subject Hello body Hi from action agent"].map((t) => (
              <button key={t} type="button" onClick={() => onSuggest(t)}
                data-testid="ai-do-suggestion"
                className="block mx-auto px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-foreground/75">
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
      {messages.map((m, i) => (
        m.kind === "user" ? (
          <div key={i} className="flex justify-end">
            <div className="max-w-[85%] rounded-xl px-3 py-2 text-sm bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white"
                 data-testid="ai-do-msg-user">
              <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
            </div>
          </div>
        ) : (
          <ActionCard key={i} payload={m.payload} onConfirm={onConfirm} onCancel={onCancel} busy={busy} />
        )
      ))}
      {busy && (
        <div className="flex items-center gap-2 text-xs text-foreground/50" data-testid="ai-do-typing">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Action agent thinking...</span>
        </div>
      )}
    </>
  );
}

/* Renders every possible action response state */
function ActionCard({ payload, onConfirm, onCancel, busy }) {
  const { status, tool, impact, message, action_id, result, confirmation_summary } = payload || {};

  if (status === "executed") {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 space-y-1.5"
           data-testid="ai-do-result-card">
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-300" />
          <span className="font-medium text-emerald-100">{tool}</span>
          <span className="text-[10px] uppercase text-emerald-300/70 tracking-widest ml-auto">executed</span>
        </div>
        <p className="text-xs text-emerald-100/80">
          {(result && result.message) || "Action completed."}
        </p>
        {result?.stats && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            <StatChip label="Attendance days" value={result.stats.attendance_days_this_month} />
            <StatChip label="Pending leaves" value={result.stats.pending_leave_requests} />
            <StatChip label="Open tasks" value={result.stats.open_tasks} />
          </div>
        )}
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 space-y-1.5"
           data-testid="ai-do-denied-card">
        <div className="flex items-center gap-2 text-sm">
          <XCircle className="h-4 w-4 text-rose-300" />
          <span className="font-medium text-rose-100">{tool || "action"}</span>
          <span className="text-[10px] uppercase text-rose-300/70 tracking-widest ml-auto">
            {impact === "blocked" ? "blocked" : "denied"}
          </span>
        </div>
        <p className="text-xs text-rose-100/80">{message}</p>
      </div>
    );
  }

  if (status === "cancelled") {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-foreground/60"
           data-testid="ai-do-cancelled-card">
        <span className="uppercase tracking-widest text-[9px] mr-2">Cancelled</span>
        {tool} was not run.
      </div>
    );
  }

  if (status === "no_tool") {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 space-y-1"
           data-testid="ai-do-notool-card">
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-300" />
          <span className="font-medium text-amber-100">No matching action</span>
        </div>
        <p className="text-xs text-amber-100/80">{message}</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-100"
           data-testid="ai-do-error-card">
        <AlertTriangle className="h-4 w-4 inline mr-1 -mt-0.5" />
        {message}
      </div>
    );
  }

  if (status === "pending" && confirmation_summary) {
    return (
      <ConfirmCard tool={tool} summary={confirmation_summary} actionId={action_id}
                   busy={busy} onConfirm={onConfirm} onCancel={onCancel} />
    );
  }

  return null;
}

function StatChip({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-2">
      <div className="text-[9px] uppercase tracking-widest text-foreground/45">{label}</div>
      <div className="text-lg font-heading text-white">{value ?? "-"}</div>
    </div>
  );
}

function ConfirmCard({ tool, summary, actionId, busy, onConfirm, onCancel }) {
  const [expanded, setExpanded] = useState(false);
  const isEmail = tool === "send_email";
  const recipients = summary.recipients || [];
  const anyExternal = summary.external_count > 0;
  const bodyLen = summary.body_length || 0;
  const bodyShown = expanded || bodyLen <= 200
    ? (summary.body_full || "")
    : (summary.body_full || "").slice(0, 200);
  const btnLabel = isEmail ? "Send email" : `Run ${tool}`;

  return (
    <div className="rounded-xl border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10 p-3 space-y-2"
         data-testid="ai-do-confirm-card">
      <div className="flex items-center gap-2 text-sm">
        {isEmail ? <Mail className="h-4 w-4 text-fuchsia-300" /> : <Zap className="h-4 w-4 text-fuchsia-300" />}
        <span className="font-medium text-fuchsia-100">Confirm: {tool}</span>
        <span className="text-[10px] uppercase text-fuchsia-300/70 tracking-widest ml-auto">
          Medium risk
        </span>
      </div>

      {isEmail ? (
        <div className="space-y-1.5 text-xs">
          <div>
            <span className="text-foreground/50 uppercase tracking-widest text-[9px] block mb-1">To</span>
            <div className="flex flex-wrap gap-1">
              {recipients.map((r, i) => (
                <span
                  key={i}
                  data-testid={r.external ? "ai-do-recipient-external" : "ai-do-recipient-internal"}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    r.external
                      ? "bg-rose-500/15 border-rose-500/40 text-rose-200"
                      : "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
                  }`}
                >
                  {r.email}{r.external ? " (external)" : ""}
                </span>
              ))}
            </div>
          </div>
          <div>
            <span className="text-foreground/50 uppercase tracking-widest text-[9px] block mb-1">Subject</span>
            <div className="text-foreground/90" data-testid="ai-do-subject">{summary.subject || "(none)"}</div>
          </div>
          <div>
            <span className="text-foreground/50 uppercase tracking-widest text-[9px] block mb-1">Body ({bodyLen} chars)</span>
            <div
              className="whitespace-pre-wrap text-foreground/85 leading-relaxed rounded bg-black/25 p-2 border border-white/5 max-h-[200px] overflow-y-auto"
              data-testid="ai-do-body-preview"
            >
              {bodyShown}{!expanded && bodyLen > 200 ? " ..." : ""}
            </div>
            {bodyLen > 200 && (
              <button type="button" onClick={() => setExpanded(!expanded)}
                className="text-[10px] mt-1 text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline"
                data-testid="ai-do-body-expand">
                {expanded ? "Collapse" : "Expand"}
              </button>
            )}
          </div>
          {anyExternal && (
            <div className="text-[10px] text-rose-300 flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>External domain(s) detected. Only CEO / ADMIN can send.</span>
            </div>
          )}
        </div>
      ) : (
        <pre className="text-[10px] text-foreground/70 bg-black/25 border border-white/5 rounded p-2 whitespace-pre-wrap max-h-[160px] overflow-y-auto">
          {JSON.stringify(summary, null, 2)}
        </pre>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" disabled={busy} onClick={() => onConfirm(actionId)}
                data-testid="ai-do-confirm-btn"
                className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white h-8">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> {btnLabel}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel(actionId)}
                data-testid="ai-do-cancel-btn"
                className="text-foreground/70 hover:text-foreground h-8">
          Cancel
        </Button>
      </div>
    </div>
  );
}
